"use client";
import { createContext, PropsWithChildren, useContext } from "react";
import { S3FeatureControl } from "./types";

const S3FeatureControlContext = createContext<S3FeatureControl | null>(null);

/**
 * S3FeatureControlProvider component
 * @param children - children: React children components
 * @param s3FeatureControl - S3FeatureControl object containing feature control data
 * @returns a Provider component that wraps the children with the S3FeatureControl context
 */
export function S3FeatureControlProvider({
  children,
  s3FeatureControl,
}: PropsWithChildren<{ s3FeatureControl: S3FeatureControl }>) {
  return (
    <S3FeatureControlContext.Provider value={createProxy(s3FeatureControl)}>
      {children}
    </S3FeatureControlContext.Provider>
  );
}

/**
 * useS3GlobalFeatureControl hook
 * @returns the S3FeatureControl context value
 * @throws an error if the context is not used within a Provider
 */
export function useS3GlobalFeatureControl() {
  const context = useContext(S3FeatureControlContext);
  if (!context) {
    throw new Error("useS3FeatureControl must be used within a Provider");
  }
  return context;
}

/**
 * useS3FeatureControl hook
 * @param key key of the S3FeatureControl object
 * @returns the value of the specified key in S3FeatureControl
 */
export function useS3FeatureControl<K extends keyof S3FeatureControl>(
  key: K
): S3FeatureControl[K] {
  return useS3GlobalFeatureControl()[key];
}

const defaultFeatureControl = { isEnabled: false, config: null };
function createProxy(value: S3FeatureControl) {
  return new Proxy(value, {
    get(obj, key: string) {
      return obj[key] ?? { id: key, ...defaultFeatureControl };
    },
  });
}
